import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Fire, Rocket, Star, Lightning } from '@phosphor-icons/react';
import { useStars } from '@/contexts/StarContext';
import { useChildTokens } from '@/components/child';

/**
 * TeenUpgradeOnboarding — visas EN gång vid byte från barn-persona till teen.
 *
 * Designen hämtar stjärndata från StarContext, beräknar Level och XP, och
 * presenterar det med teen-estetik: mörk bakgrund, neon-glow, pulsande level-
 * badge, uppräknande XP-counter och subtil confetti-partikelkänsla.
 *
 * localStorage-flagga: 'teen-onboarding-seen'. TeenHome.tsx gatear på denna.
 */

const STORAGE_KEY = 'teen-onboarding-seen';

/** CSS keyframes for animations */
const keyframes = `
@keyframes teenOnbLevelPulse {
  0%, 100% { transform: scale(1); box-shadow: 0 0 24px rgba(110, 143, 184, 0.4); }
  50% { transform: scale(1.08); box-shadow: 0 0 40px rgba(110, 143, 184, 0.7); }
}
@keyframes teenOnbFadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes teenOnbConfetti {
  0% { transform: translateY(0) rotate(0deg); opacity: 1; }
  100% { transform: translateY(-60px) rotate(360deg); opacity: 0; }
}
@keyframes teenOnbShimmer {
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
}
`;

type Props = {
  onComplete: () => void;
};

const TeenUpgradeOnboarding = ({ onComplete }: Props) => {
  const { t } = useTranslation();
  const tokens = useChildTokens();
  const { balance, streak } = useStars();

  const totalStars = balance.totalEarnedStars;
  const level = Math.floor(totalStars / 5) + 1;
  const xp = totalStars * 10;
  const currentStreak = streak.currentStreak;

  // Animated XP counter
  const [displayedXp, setDisplayedXp] = useState(0);
  const animStarted = useRef(false);

  useEffect(() => {
    if (animStarted.current) return;
    animStarted.current = true;

    const duration = 1500; // ms
    const startTime = performance.now();
    const targetXp = xp;

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayedXp(Math.round(targetXp * eased));
      if (progress < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }, [xp]);

  const handleComplete = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    onComplete();
  };

  // Confetti particles (decorative)
  const confettiColors = useMemo(() => ['#6E8FB8', '#6FA886', '#D2AC63', '#C25B5B', '#9AA3B2'], []);

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        bgcolor: tokens.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Inject keyframes */}
      <Box component="style">{keyframes}</Box>

      {/* Decorative confetti particles */}
      {confettiColors.map((color, i) => (
        <Box
          key={i}
          aria-hidden
          sx={{
            position: 'absolute',
            width: 8,
            height: 8,
            borderRadius: i % 2 === 0 ? '50%' : '2px',
            bgcolor: color,
            top: `${15 + i * 12}%`,
            left: `${10 + i * 18}%`,
            opacity: 0.7,
            '@media (prefers-reduced-motion: no-preference)': {
              animation: `teenOnbConfetti ${2 + i * 0.4}s ease-out ${i * 0.3}s infinite`,
            },
          }}
        />
      ))}

      <Container maxWidth="sm">
        <Stack
          spacing={4}
          alignItems="center"
          textAlign="center"
          sx={{
            py: 6,
            px: 2,
            '@media (prefers-reduced-motion: no-preference)': {
              animation: 'teenOnbFadeUp 0.6s ease-out both',
            },
          }}
        >
          {/* Level badge — hero element with glow pulse */}
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              bgcolor: tokens.card,
              border: '3px solid',
              borderColor: 'primary.main',
              display: 'grid',
              placeItems: 'center',
              position: 'relative',
              '@media (prefers-reduced-motion: no-preference)': {
                animation: 'teenOnbLevelPulse 2.5s ease-in-out infinite',
              },
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: '2rem',
                  color: tokens.text,
                  lineHeight: 1,
                }}
              >
                {level}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  color: tokens.textSoft,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                level
              </Typography>
            </Box>
          </Box>

          {/* Hero text */}
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: '1.6rem',
              color: tokens.text,
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
            }}
          >
            {t('teen.onboarding.heroTitle', { level })}
          </Typography>

          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '1rem',
              color: tokens.textSoft,
              maxWidth: 300,
            }}
          >
            {t('teen.onboarding.heroSubtitle')}
          </Typography>

          {/* XP conversion card */}
          <Box
            sx={{
              bgcolor: tokens.card,
              border: '1px solid',
              borderColor: tokens.border,
              borderRadius: '16px',
              p: '20px 28px',
              width: '100%',
              maxWidth: 320,
              '@media (prefers-reduced-motion: no-preference)': {
                animation: 'teenOnbFadeUp 0.6s ease-out 0.3s both',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
              <Star size={22} weight="fill" color={tokens.orange} />
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: tokens.text }}>
                {totalStars} {t('teen.onboarding.stars')}
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: '1.1rem', color: tokens.textSoft }}>
                →
              </Typography>
              <Lightning size={22} weight="fill" color="primary.main" />
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '1.1rem',
                  color: tokens.text,
                  background: 'linear-gradient(90deg, #6E8FB8 0%, #6FA886 50%, #6E8FB8 100%)',
                  backgroundSize: '200% auto',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  '@media (prefers-reduced-motion: no-preference)': {
                    animation: 'teenOnbShimmer 3s linear infinite',
                  },
                }}
              >
                {displayedXp} XP
              </Typography>
            </Box>
          </Box>

          {/* Streak card */}
          {currentStreak > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                bgcolor: tokens.card,
                border: '1px solid',
                borderColor: tokens.border,
                borderRadius: '14px',
                p: '16px 24px',
                '@media (prefers-reduced-motion: no-preference)': {
                  animation: 'teenOnbFadeUp 0.6s ease-out 0.5s both',
                },
              }}
            >
              <Fire size={24} weight="fill" color="#F59E0B" />
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: tokens.text }}>
                {t('teen.onboarding.streak', { count: currentStreak })}
              </Typography>
            </Box>
          )}

          {/* CTA button */}
          <Box
            component="button"
            type="button"
            onClick={handleComplete}
            sx={{
              mt: 2,
              width: '100%',
              maxWidth: 300,
              p: '18px 36px',
              borderRadius: '16px',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              border: 'none',
              fontWeight: 800,
              fontSize: '1.1rem',
              cursor: 'pointer',
              fontFamily: 'inherit',
              boxShadow: '0 4px 20px rgba(110, 143, 184, 0.35)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              '@media (hover: hover)': {
                '&:hover': {
                  transform: 'scale(1.03)',
                  boxShadow: '0 6px 28px rgba(110, 143, 184, 0.5)',
                },
              },
              '&:active': { transform: 'scale(0.97)' },
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
              },
              '@media (prefers-reduced-motion: no-preference)': {
                animation: 'teenOnbFadeUp 0.6s ease-out 0.7s both',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <Rocket size={20} weight="fill" />
              <span>{t('teen.onboarding.cta')}</span>
            </Box>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default TeenUpgradeOnboarding;
